import { useState } from 'react';
import { createFinanceContactRequest, ContactKind, ContactType, organizationTypeLabel } from '@signalvote/shared';
import type { OrganizationType } from '@signalvote/shared';
import type { CreateFinanceContactRequest } from '@signalvote/shared';
import { useCreateFinanceContact } from '@/hooks/queries/useFinanceContacts';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  financeFormInputClass,
  financeFormSelectClass,
  financeFormLabelClass,
} from '@/components/finance/shared/financeControlClasses';
import { creatableOrganizationTypes } from '@/components/finance/entry/entryParts';
import {
  EMPTY_EMPLOYER_ADDRESS,
  EmployerAddressFields,
  employerAddressRequest,
} from './EmployerAddressFields';
import { readyProfile, profileUnreadyMessage } from '@/lib/complianceProfileRead';

interface AddContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddContactDialog({ open, onOpenChange }: AddContactDialogProps) {
  const { mutateAsync: createContact, isPending } = useCreateFinanceContact();
  const profileRead = useComplianceProfile();
  const complianceProfile = readyProfile(profileRead);
  const profileUnready = profileUnreadyMessage(profileRead);
  const employerAddressRequired = complianceProfile?.employerAddressRequired ?? false;
  const [contactKind, setContactKind] = useState<ContactKind>(ContactKind.Individual);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameSuffix, setNameSuffix] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationType, setOrganizationType] = useState<OrganizationType | ''>('');
  const [contactType, setContactType] = useState<ContactType>(ContactType.Donor);
  const [address, setAddress] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [employer, setEmployer] = useState('');
  const [occupation, setOccupation] = useState('');
  const [employmentLocation, setEmploymentLocation] = useState('');
  const [employerAddress, setEmployerAddress] = useState(EMPTY_EMPLOYER_ADDRESS);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSave = contactKind === ContactKind.Individual
    ? firstName.trim().length > 0 && lastName.trim().length > 0
    : organizationName.trim().length > 0 && organizationType !== '';
  const organizationTypeOptions = creatableOrganizationTypes(complianceProfile?.supportedOrganizationTypes ?? []);

  const resetForm = () => {
    setContactKind(ContactKind.Individual);
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setNameSuffix('');
    setOrganizationName('');
    setOrganizationType('');
    setContactType(ContactType.Donor);
    setAddress('');
    setAddressLine2('');
    setCity('');
    setState('');
    setZip('');
    setEmail('');
    setPhone('');
    setEmployer('');
    setOccupation('');
    setEmploymentLocation('');
    setEmployerAddress(EMPTY_EMPLOYER_ADDRESS);
    setDuplicateWarning(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!canSave) return;
    setError(null);
    setDuplicateWarning(null);

    const request: CreateFinanceContactRequest = createFinanceContactRequest({
      contactType,
      contactKind,
      firstName: contactKind === ContactKind.Individual ? firstName.trim() : null,
      middleName: contactKind === ContactKind.Individual ? (middleName.trim() || null) : null,
      lastName: contactKind === ContactKind.Individual ? lastName.trim() : null,
      nameSuffix: contactKind === ContactKind.Individual ? (nameSuffix.trim() || null) : null,
      organizationName: contactKind === ContactKind.Organization ? organizationName.trim() : null,
      organizationType: contactKind === ContactKind.Organization && organizationType !== '' ? organizationType : null,
      address: address.trim() || null,
      addressLine2: addressLine2.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      zipCode: zip.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      employer: employer.trim() || null,
      occupation: occupation.trim() || null,
      employmentLocation: employmentLocation.trim() || null,
      ...employerAddressRequest(employerAddress),
    });

    try {
      const result = await createContact(request);
      if (result.duplicateWarning) {
        setDuplicateWarning(result.duplicateWarning);
        return;
      }
      resetForm();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const inputCls = cn(financeFormInputClass, 'w-full');
  const selectCls = cn(financeFormSelectClass, 'w-full');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-contact-dialog">
        <DialogHeader>
          <DialogTitle>Add contact</DialogTitle>
        </DialogHeader>
        {profileUnready && (
          <p role="alert" className="text-sm text-destructive">
            {profileUnready} The required fields and organization types come from it.
          </p>
        )}

        <div className="space-y-3">
          <div>
            <label htmlFor="add-contact-kind" className={financeFormLabelClass}>Kind</label>
            <select
              id="add-contact-kind"
              value={contactKind}
              onChange={(e) => setContactKind(e.target.value as ContactKind)}
              className={selectCls}
            >
              <option value={ContactKind.Individual}>Individual</option>
              <option value={ContactKind.Organization}>Organization</option>
            </select>
          </div>

          {contactKind === ContactKind.Individual ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="add-contact-first-name" className={financeFormLabelClass}>First name *</label>
                <input id="add-contact-first-name" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label htmlFor="add-contact-middle-name" className={financeFormLabelClass}>Middle</label>
                <input id="add-contact-middle-name" type="text" value={middleName} onChange={(e) => setMiddleName(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label htmlFor="add-contact-last-name" className={financeFormLabelClass}>Last name *</label>
                <input id="add-contact-last-name" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label htmlFor="add-contact-suffix" className={financeFormLabelClass}>Suffix</label>
                <input id="add-contact-suffix" type="text" value={nameSuffix} onChange={(e) => setNameSuffix(e.target.value)} className={inputCls} placeholder="Jr., III" />
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="add-contact-org-name" className={financeFormLabelClass}>Organization name *</label>
              <input id="add-contact-org-name" type="text" value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} className={inputCls} />
              <label htmlFor="add-contact-org-type" className={cn(financeFormLabelClass, 'mt-2')}>Organization type *</label>
              <select
                id="add-contact-org-type"
                value={organizationType}
                onChange={(e) => setOrganizationType(e.target.value as OrganizationType | '')}
                className={selectCls}
              >
                <option value="">Select a type</option>
                {organizationTypeOptions.map((value) => (
                  <option key={value} value={value}>{organizationTypeLabel(value)}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="add-contact-type" className={financeFormLabelClass}>Type</label>
            <select id="add-contact-type" value={contactType} onChange={(e) => setContactType(e.target.value as ContactType)} className={selectCls}>
              <option value={ContactType.Donor}>Donor</option>
              <option value={ContactType.Payee}>Payee</option>
            </select>
          </div>

          <div>
            <label htmlFor="add-contact-address" className={financeFormLabelClass}>Street address</label>
            <input id="add-contact-address" type="text" value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} />
          </div>

          <div>
            <label htmlFor="add-contact-address2" className={financeFormLabelClass}>Address line 2</label>
            <input id="add-contact-address2" data-testid="add-contact-address2" type="text" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} placeholder="Apt, suite, floor (optional)" className={inputCls} />
          </div>

          <div>
            <label htmlFor="add-contact-city" className={financeFormLabelClass}>City</label>
            <input id="add-contact-city" type="text" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="add-contact-state" className={financeFormLabelClass}>State</label>
              <input id="add-contact-state" type="text" value={state} onChange={(e) => setState(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="add-contact-zip" className={financeFormLabelClass}>ZIP</label>
              <input id="add-contact-zip" type="text" value={zip} onChange={(e) => setZip(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="add-contact-email" className={financeFormLabelClass}>Email</label>
              <input id="add-contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="add-contact-phone" className={financeFormLabelClass}>Phone</label>
              <input id="add-contact-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="add-contact-employer" className={financeFormLabelClass}>Employer</label>
              <input id="add-contact-employer" type="text" value={employer} onChange={(e) => setEmployer(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="add-contact-occupation" className={financeFormLabelClass}>Occupation</label>
              <input id="add-contact-occupation" type="text" value={occupation} onChange={(e) => setOccupation(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div>
            <label htmlFor="add-contact-employment-location" className={financeFormLabelClass}>Employment location</label>
            <input id="add-contact-employment-location" type="text" value={employmentLocation} onChange={(e) => setEmploymentLocation(e.target.value)} placeholder="City, State" className={inputCls} />
          </div>

          {employerAddressRequired && (
            <div className="grid grid-cols-2 gap-2">
              <EmployerAddressFields
                idPrefix="add-contact"
                value={employerAddress}
                onChange={setEmployerAddress}
                labelClassName={financeFormLabelClass}
                inputClassName={inputCls}
              />
            </div>
          )}

          {duplicateWarning && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {duplicateWarning}
              <p className="mt-1 text-xs">Contact was created. You can close this dialog.</p>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        {/* Footer rule (shared with the ledger detail panels): the safe secondary
            (Cancel / Close) is a quiet ghost link above the full-width primary. */}
        <div className="mt-4 border-t pt-3">
          <div className="mb-2 text-center">
            <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
              {duplicateWarning ? 'Close' : 'Cancel'}
            </Button>
          </div>
          {!duplicateWarning && (
            <Button
              type="button"
              className="w-full"
              onClick={handleSave}
              disabled={!canSave || isPending}
            >
              {isPending ? 'Saving…' : 'Save contact'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
